import styled from '@emotion/styled'
import type { CurriculumApi } from '@mathflat/domain/@entities/Curriculum/api'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'

import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useRepository } from '~/@common/hooks/useRepository'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import { colors, fontWeight, typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import Button from '~/@common/ui/(Button)/Button/Button'
import IconButton from '~/@common/ui/(Button)/IconButton/IconButton'
import SearchInput from '~/@common/ui/(Input)/SearchInput/SearchInput'
import { Dropdown, DropdownItem } from '~/@common/ui/Dropdown'
import { mixpanelClient } from '~/@common/utils/trackings/mixpanel'
import { WorkbookSearchService } from '~/@pages/@widgets/WorkbookSearchModal/WorkbookSearch.service'

import { ChapterSearchService } from './ChapterSearchService'

type Props = {
  onSubmit: (trieKey: string, middleChapterId: number) => Promise<void>
}

const ChapterSearchModal = ({ onSubmit }: Props) => {
  const workbookSearchService = useRepository(WorkbookSearchService)
  const chapterSearchService = useRepository(ChapterSearchService)
  const { isMobile } = useStudentAppMediaQuery()

  const gradeTrieKey = (() => {
    const curriculumCore = commonRepo.curriculumCore

    if (!curriculumCore?.allGradeSemester) return null

    if (!workbookSearchService.curriculumKey) {
      return curriculumCore.allGradeSemester[0].upperTrieKey
    }

    const curriculum = curriculumCore.allGradeSemester.find(
      ({ trieKey }) => trieKey === workbookSearchService.curriculumKey,
    )
    return curriculum?.schoolType === 'HIGH' ? curriculum?.trieKey : curriculum?.upperTrieKey
  })()

  const [selectedTrieKey, setSelectedTrieKey] = useState<string | null>(gradeTrieKey ?? null)

  const [selectedChapter, setSelectedChapter] = useState<
    CurriculumApi.Response.ChaptersBySearch['chapters'][number] | null
  >(null)

  const [searchValue, setSearchValue] = useState('')

  useEffect(() => {
    return () => {
      chapterSearchService.resetChapterList()
    }
  }, [])

  const iconButton = (
    <IconButton
      name="icon_close"
      iconSize={20}
      iconStyle={{
        color: isMobile ? colors.gray.$900 : colors.gray.$500,
      }}
      onClick={() => {
        modalService.closeModal()
        mixpanelClient.track('단원검색모달_닫기버튼_Click')
      }}
    />
  )
  return (
    <S.Container>
      <S.Header>
        {isMobile && <div className="title">검색 {iconButton}</div>}
        <div className="search-input-container">
          <Dropdown
            width={isMobile ? 120 : 156}
            value={selectedTrieKey}
            onChange={(trieKey) => {
              setSearchValue('')
              setSelectedTrieKey(trieKey)
            }}
          >
            {commonRepo.curriculumCore?.allGrade.map((grade) => (
              <DropdownItem key={grade.trieKey} value={grade.trieKey}>
                {grade.labelShort}
              </DropdownItem>
            ))}
          </Dropdown>
          <div className="search-input">
            <SearchInput
              placeholder="대단원을 검색해보세요."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onSearch={async (search) => {
                if (!selectedTrieKey || search.trim() === '') {
                  return
                }

                await chapterSearchService.fetchChapterList(selectedTrieKey, search)
                setSelectedChapter(null)

                mixpanelClient.track('단원검색하기버튼_Click', {
                  단원검색어: search,
                })
              }}
            />
          </div>
          {!isMobile && iconButton}
        </div>
      </S.Header>
      <S.Content>
        <ChapterList
          chapterList={chapterSearchService.chapterList}
          selectedChapter={selectedChapter}
          setSelectedChapter={setSelectedChapter}
        />
      </S.Content>
      <S.Footer>
        <Button
          size="small"
          theme="primary"
          minWidth={160}
          disabled={!selectedChapter}
          onClick={() => {
            if (selectedChapter) {
              const { semesterSubjectTrieKey, middleChapterId } = selectedChapter
              workbookSearchService.setCurriculumKey(semesterSubjectTrieKey)
              onSubmit(semesterSubjectTrieKey, middleChapterId)
              modalService.closeModal()

              mixpanelClient.track('단원검색어선택하기버튼_Click', {
                단원검색어: searchValue,
              })
            }
          }}
        >
          선택하기
        </Button>
      </S.Footer>
    </S.Container>
  )
}

export default observer(ChapterSearchModal)

const ChapterList = ({
  chapterList,
  selectedChapter,
  setSelectedChapter,
}: {
  chapterList: CurriculumApi.Response.ChaptersBySearch['chapters'] | null
  selectedChapter: CurriculumApi.Response.ChaptersBySearch['chapters'][number] | null
  setSelectedChapter: (chapter: CurriculumApi.Response.ChaptersBySearch['chapters'][number]) => void
}) => {
  if (!chapterList) {
    return (
      <div className="empty-list">
        22개정에서 일부 단원 순서가 다를 수 있어요!
        <br /> 챌린지에서 내가 찾는 단원이 없다면 검색해보세요.
      </div>
    )
  }

  if (chapterList.length === 0) {
    return (
      <div className="empty-list">
        검색한 단원이 없습니다.
        <br />
        다시 입력해주세요.
      </div>
    )
  }

  return (
    <div className="chapter-list-container">
      <div className="chapter-list-header">대단원 &gt; 중단원</div>
      <div className="chapter-list-content">
        {chapterList.map((chapter) => {
          const { bigChapterName, middleChapterName } = chapter
          const curriculumItem = commonRepo.curriculumCore?.getItemByTrieKey(
            chapter.semesterSubjectTrieKey,
          )

          return (
            <div
              key={chapter.middleChapterId}
              className={clsx(
                'chapter-list-item',
                selectedChapter?.middleChapterId === chapter.middleChapterId && 'selected',
              )}
              onClick={() => setSelectedChapter(chapter)}
            >
              <span>{curriculumItem?.label} &gt;</span>
              <span className="accent">{bigChapterName} </span>
              <span>&gt; {middleChapterName}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    width: 600px;
    height: 432px;

    ${mediaQuery.underTablet} {
      position: fixed;
      inset: 0;
      width: 100%;
      height: 100%;
      background-color: #fff;
      padding: var(--g-safe-area-top) var(--g-safe-area-right) var(--g-safe-area-bottom)
        var(--g-safe-area-left);
    }
  `,

  Header: styled.header`
    display: flex;
    flex-direction: column;

    .search-input-container {
      display: flex;
      gap: 10px;
      padding: 14px 24px;
      border-bottom: 1px solid ${colors.gray.$200};
    }

    .search-input {
      display: flex;
      flex: 1;
      height: 40px;
      border: 1px solid ${colors.gray.$400};
      border-radius: var(--Radius-100);
      margin-right: 14px;
      overflow: hidden;

      .ms__Input_input {
        border: none;
      }

      > div {
        display: flex;
      }
      button {
        padding: 0px 10px;
        background: ${colors.gray.$100};
        border-left: 1px solid ${colors.gray.$400};
      }
    }

    ${mediaQuery.underTablet} {
      .title {
        height: 61px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        ${typo.body02};
        border-bottom: 1px solid ${colors.gray.$300};
        color: ${colors.gray.$900};
        font-weight: ${fontWeight.bold};
        > button {
          position: absolute;
          right: 24px;
          top: 50%;
          margin-top: -10px;
        }
      }
      .search {
        input::placeholder {
          ${typo.body02};
        }
      }

      .search-input {
        margin-right: 0px;
      }
    }
  `,

  Content: styled.div`
    display: flex;
    width: 100%;
    overflow: hidden;
    flex: 1;

    .empty-list {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      text-align: center;
      ${typo.body02};
      color: ${colors.gray.$600};
    }

    .chapter-list-container {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    .chapter-list-header {
      ${typo.caption01};
      width: 100%;
      padding: 8px 24px;
      border-bottom: 1px solid ${colors.gray.$200};
    }

    .chapter-list-content {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      overflow-y: scroll;
    }

    .chapter-list-item {
      padding: 20px 24px;
      ${typo.body01};
      border-bottom: 1px solid ${colors.gray.$200};
      cursor: pointer;

      span {
        ${typo.body01};
        color: ${colors.gray.$900};

        &.accent {
          color: ${colors.blue.$500};
        }
      }

      &:hover {
        background-color: ${colors.gray.$100};
      }

      &:last-child {
        border-bottom: none;
      }

      &.selected {
        background: rgba(57, 95, 226, 0.05);
      }
    }
  `,

  Footer: styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 14px 24px;
    border-top: 1px solid ${colors.gray.$200};
    button {
      display: flex;
      align-items: center;
      gap: 2px;
      ${typo.body02};
      color: ${colors.gray.$600};
      cursor: pointer;
    }

    ${mediaQuery.underTablet} {
      flex-direction: column;
      gap: 20px;
      padding: 14px 24px;
      border-top: 1px solid ${colors.gray.$200};

      button {
        width: 100%;
      }
    }
  `,
}
